import { useCreateExportJobMutation } from 'src/graphql/_generated';
import { showSuccessToast, showErrorToast, toastMessages } from 'src/components/Toast';

// Mirrors the export-runner's DocConfigSchema discriminated union — kept as a
// hand-rolled type because the runner's Zod schema isn't shared across repos.
// Runner source: export-runner-server/export-runner/src/config/docConfig.ts.

export interface IncludesActions {
  enabled: boolean;
  completionStatus: 'ALL' | 'COMPLETED' | 'INCOMPLETED';
}

export interface AssessmentDocConfig {
  type: 'assessment';
  format: 'pdf';
  assessmentId: string;
  isLogarithmic: boolean;
  // Empty array means "all scenarios" on the runner side.
  renderScenarios: string[];
  includesExecSummary: boolean;
  includesScenarios: boolean;
  includesRMP: boolean;
  includesIncidents: boolean;
  includesRiskTolerance: boolean;
  includesActions: IncludesActions;
}

export interface ScenarioDocConfig {
  type: 'scenario';
  format: 'pdf';
  scenarioId: string;
  // Used by the runner only for the external title link; not for data fetching.
  assessmentId: string;
  isLogarithmic: boolean;
  includesRMP: boolean;
  includesIncidents: boolean;
  includesActions: IncludesActions;
}

export interface RmpDocConfig {
  type: 'rmp';
  format: 'pdf';
  controlIds: string[];
  includesActions: IncludesActions;
}

export interface InsightsDocConfig {
  type: 'insights';
  format: 'pdf';
  assessmentId: string;
}

export type ExportDocConfig =
  | AssessmentDocConfig
  | ScenarioDocConfig
  | RmpDocConfig
  | InsightsDocConfig;

interface UseExportJobOptions {
  // Closed only on success; on failure the modal stays open so the user keeps
  // their selections and can retry.
  onClose: () => void;
}

export function useExportJob({ onClose }: UseExportJobOptions) {
  const [createExportJob, { loading }] = useCreateExportJobMutation({
    onCompleted: () => {
      showSuccessToast(toastMessages.export.requested);
      onClose();
    },
    onError: () => {
      // showErrorToast takes a string, not the {title, subtitle} object the
      // success toast uses — pass the title only.
      showErrorToast(toastMessages.export.failed.title);
    },
  });

  const triggerExport = (docConfig: ExportDocConfig) => {
    return createExportJob({
      variables: {
        reportType: docConfig.type,
        reportParams: JSON.stringify(docConfig),
      },
    });
  };

  return { triggerExport, loading };
}
